import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, MessageSquare, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, STATUS_STYLES, PRIORITY_STYLES, COMPLIANCE_COLORS, fromNow } from "@/lib/format";

const STATUSES = [
    "created",
    "assigned",
    "in_progress",
    "submitted_for_review",
    "returned_for_rework",
    "review",
    "completed",
    "closed",
];
const EMPLOYEE_STATUSES = ["in_progress", "submitted_for_review"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const COMPLIANCE_TYPES = ["GST", "ITR", "TDS", "ROC", "Audit", "PF", "ESI", "Custom"];

const EMPTY = {
    title: "",
    description: "",
    client_id: "",
    compliance_type: "GST",
    assigned_to: "",
    priority: "medium",
    status: "created",
    official_deadline: "",
    internal_deadline: "",
    checklist: [],
};

export default function Tasks() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [view, setView] = useState("board");
    const [q, setQ] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterPriority, setFilterPriority] = useState("all");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [detail, setDetail] = useState(null);
    const [comment, setComment] = useState("");
    const [reworkOpen, setReworkOpen] = useState(false);
    const [reworkReason, setReworkReason] = useState("");

    const load = async () => {
        const { data } = await api.get("/tasks", { params: { q: q || undefined } });
        setItems(data.items);
    };

    useEffect(() => {
        load();
        api.get("/clients").then((r) => setClients(r.data.items)).catch(() => {});
        if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const filtered = useMemo(() => {
        return items.filter((t) => {
            if (filterStatus !== "all" && t.status !== filterStatus) return false;
            if (filterPriority !== "all" && t.priority !== filterPriority) return false;
            return true;
        });
    }, [items, filterStatus, filterPriority]);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
    const empName = (id) => employees.find((e) => e.id === id)?.name || "";

    const save = async () => {
        try {
            const payload = { ...form };
            if (!payload.assigned_to) delete payload.assigned_to;
            if (editing) await api.patch(`/tasks/${editing.id}`, payload);
            else await api.post("/tasks", payload);
            toast.success(editing ? "Task updated" : "Task created");
            setOpen(false);
            setEditing(null);
            setForm(EMPTY);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const changeStatus = async (t, status) => {
        try {
            await api.patch(`/tasks/${t.id}`, { status });
            load();
            if (detail?.id === t.id) setDetail({ ...detail, status });
            if (status === "submitted_for_review") {
                toast.success("Submitted for review — admin has been notified");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const approveTask = async () => {
        try {
            await api.post(`/tasks/${detail.id}/approve`);
            toast.success("Approved & closed");
            setDetail(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const submitRework = async () => {
        if (!reworkReason.trim()) {
            toast.error("Please add a reason for rework.");
            return;
        }
        try {
            await api.post(`/tasks/${detail.id}/rework`, { text: reworkReason });
            toast.success("Task returned for rework");
            setReworkOpen(false);
            setReworkReason("");
            setDetail(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const addComment = async () => {
        if (!comment.trim() || !detail) return;
        try {
            const { data } = await api.post(`/tasks/${detail.id}/comments`, { text: comment });
            setDetail({ ...detail, comments: [...(detail.comments || []), data] });
            setComment("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const openDetail = async (t) => {
        try {
            const { data } = await api.get(`/tasks/${t.id}`);
            setDetail(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const grouped = useMemo(() => {
        const g = Object.fromEntries(STATUSES.map((s) => [s, []]));
        filtered.forEach((t) => (g[t.status] || (g[t.status] = [])).push(t));
        return g;
    }, [filtered]);

    return (
        <div className="space-y-6" data-testid="tasks-page">
            <PageHeader
                title={isAdmin ? "Tasks" : "My Tasks"}
                subtitle={
                    isAdmin
                        ? "Manage the workflow from creation to closure"
                        : "Tasks assigned to you — update status and submit for review when done"
                }
                actions={
                    isAdmin && (
                        <Button
                            onClick={() => {
                                setEditing(null);
                                setForm(EMPTY);
                                setOpen(true);
                            }}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="new-task-btn"
                        >
                            <Plus size={16} className="mr-1.5" /> New task
                        </Button>
                    )
                }
            />

            <Card className="p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search size={16} className="text-slate-400 ml-2" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search tasks…"
                        className="border-0 focus-visible:ring-0"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40" data-testid="filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                                {s.replace("_", " ")}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                    <SelectTrigger className="w-40" data-testid="filter-priority">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All priorities</SelectItem>
                        {PRIORITIES.map((p) => (
                            <SelectItem key={p} value={p}>
                                {p}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Tabs value={view} onValueChange={setView}>
                    <TabsList>
                        <TabsTrigger value="board" data-testid="view-board">Board</TabsTrigger>
                        <TabsTrigger value="list" data-testid="view-list">List</TabsTrigger>
                    </TabsList>
                </Tabs>
            </Card>

            {view === "board" ? (
                <div className="flex gap-4 overflow-x-auto pb-2" data-testid="kanban-board">
                    {STATUSES.map((s) => (
                        <div key={s} className="min-w-[280px] bg-slate-100/50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                                    {s.replace("_", " ")}
                                </div>
                                <span className="text-xs text-slate-500 font-mono">
                                    {grouped[s].length}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {grouped[s].map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => openDetail(t)}
                                        className="w-full text-left bg-white border border-slate-200 rounded-md p-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
                                        data-testid={`kanban-card-${t.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="text-sm font-medium text-slate-800 line-clamp-2">
                                                {t.title}
                                            </div>
                                            <span
                                                className={`badge-status ${COMPLIANCE_COLORS[t.compliance_type] || ""}`}
                                            >
                                                {t.compliance_type}
                                            </span>
                                        </div>
                                        <div className="mt-2 text-xs text-slate-500 truncate">
                                            {clientName(t.client_id)}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between gap-1">
                                            <span
                                                className={`badge-status ${PRIORITY_STYLES[t.priority]}`}
                                            >
                                                {t.priority}
                                            </span>
                                            {t.official_deadline && (
                                                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                                                    <CalendarDays size={11} /> {fmtDate(t.official_deadline)}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                                {grouped[s].length === 0 && (
                                    <div className="text-xs text-slate-400 text-center py-6">Empty</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-3 font-semibold">Task</th>
                                    <th className="text-left px-4 py-3 font-semibold">Client</th>
                                    <th className="text-left px-4 py-3 font-semibold">Type</th>
                                    <th className="text-left px-4 py-3 font-semibold">Priority</th>
                                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                                    <th className="text-left px-4 py-3 font-semibold">Deadline</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((t) => (
                                    <tr
                                        key={t.id}
                                        className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer"
                                        onClick={() => openDetail(t)}
                                        data-testid={`task-row-${t.id}`}
                                    >
                                        <td className="px-4 py-3 font-medium text-slate-800">{t.title}</td>
                                        <td className="px-4 py-3 text-slate-600">{clientName(t.client_id)}</td>
                                        <td className="px-4 py-3">
                                            <span className={`badge-status ${COMPLIANCE_COLORS[t.compliance_type] || ""}`}>
                                                {t.compliance_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`badge-status ${PRIORITY_STYLES[t.priority]}`}>
                                                {t.priority}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`badge-status ${STATUS_STYLES[t.status]}`}>
                                                {t.status.replace(/_/g, " ")}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{fmtDate(t.official_deadline)}</td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-12 text-slate-500">
                                            No tasks match these filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Create / Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {editing ? "Edit task" : "New task"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <F label="Title">
                                <Input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    data-testid="task-title"
                                />
                            </F>
                        </div>
                        <F label="Client">
                            <Select
                                value={form.client_id}
                                onValueChange={(v) => setForm({ ...form, client_id: v })}
                            >
                                <SelectTrigger data-testid="task-client">
                                    <SelectValue placeholder="Choose client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Compliance type">
                            <Select
                                value={form.compliance_type}
                                onValueChange={(v) => setForm({ ...form, compliance_type: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {COMPLIANCE_TYPES.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Assigned to">
                            <Select
                                value={form.assigned_to || "unassigned"}
                                onValueChange={(v) =>
                                    setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Unassigned" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {employees.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Priority">
                            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRIORITIES.map((p) => (
                                        <SelectItem key={p} value={p}>
                                            {p}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Status">
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s.replace("_", " ")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Official deadline">
                            <Input
                                type="date"
                                value={form.official_deadline || ""}
                                onChange={(e) => setForm({ ...form, official_deadline: e.target.value })}
                            />
                        </F>
                        <F label="Internal deadline">
                            <Input
                                type="date"
                                value={form.internal_deadline || ""}
                                onChange={(e) => setForm({ ...form, internal_deadline: e.target.value })}
                            />
                        </F>
                        <div className="col-span-2">
                            <F label="Description">
                                <Textarea
                                    rows={3}
                                    value={form.description || ""}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </F>
                        </div>
                        <div className="col-span-2">
                            <div className="field-label mb-2">Checklist</div>
                            <div className="space-y-2">
                                {form.checklist.map((item, i) => (
                                    <div key={item.id} className="flex items-center gap-2">
                                        <Checkbox
                                            checked={item.done}
                                            onCheckedChange={(v) => {
                                                const cl = [...form.checklist];
                                                cl[i] = { ...cl[i], done: !!v };
                                                setForm({ ...form, checklist: cl });
                                            }}
                                        />
                                        <Input
                                            value={item.text}
                                            onChange={(e) => {
                                                const cl = [...form.checklist];
                                                cl[i] = { ...cl[i], text: e.target.value };
                                                setForm({ ...form, checklist: cl });
                                            }}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                setForm({
                                                    ...form,
                                                    checklist: form.checklist.filter((_, idx) => idx !== i),
                                                })
                                            }
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setForm({
                                            ...form,
                                            checklist: [
                                                ...form.checklist,
                                                { id: Math.random().toString(36).slice(2), text: "", done: false },
                                            ],
                                        })
                                    }
                                >
                                    + Add item
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-task">
                            {editing ? "Save changes" : "Create task"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail drawer */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">
                                    {detail.title}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={`badge-status ${COMPLIANCE_COLORS[detail.compliance_type]}`}>
                                    {detail.compliance_type}
                                </span>
                                <span className={`badge-status ${PRIORITY_STYLES[detail.priority]}`}>
                                    {detail.priority}
                                </span>
                                <Select value={detail.status} onValueChange={(v) => changeStatus(detail, v)}>
                                    <SelectTrigger className="w-52 h-7" data-testid="detail-status">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(isAdmin ? STATUSES : EMPLOYEE_STATUSES).map((s) => (
                                            <SelectItem key={s} value={s}>
                                                {s.replace(/_/g, " ")}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {!isAdmin && detail.status !== "submitted_for_review" && detail.status !== "closed" && (
                                    <Button
                                        size="sm"
                                        onClick={() => changeStatus(detail, "submitted_for_review")}
                                        className="bg-primary hover:bg-primary/90"
                                        data-testid="submit-for-review"
                                    >
                                        Submit for review
                                    </Button>
                                )}
                                {isAdmin && detail.status === "submitted_for_review" && (
                                    <>
                                        <Button
                                            size="sm"
                                            onClick={approveTask}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            data-testid="approve-task"
                                        >
                                            Approve & Close
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setReworkOpen(true)}
                                            className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                            data-testid="rework-task"
                                        >
                                            Return for rework
                                        </Button>
                                    </>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                                <Info label="Client" value={clientName(detail.client_id)} />
                                <Info label="Assignee" value={empName(detail.assigned_to) || "Unassigned"} />
                                <Info label="Official deadline" value={fmtDate(detail.official_deadline)} />
                                <Info label="Internal deadline" value={fmtDate(detail.internal_deadline)} />
                            </div>
                            {detail.description && (
                                <div className="text-sm text-slate-600 whitespace-pre-wrap border rounded-md p-3 bg-slate-50">
                                    {detail.description}
                                </div>
                            )}

                            {detail.checklist?.length > 0 && (
                                <div>
                                    <div className="field-label mb-2">Checklist</div>
                                    <div className="space-y-1.5">
                                        {detail.checklist.map((c, i) => (
                                            <label key={c.id} className="flex items-center gap-2 text-sm">
                                                <Checkbox
                                                    checked={c.done}
                                                    onCheckedChange={async (v) => {
                                                        const cl = [...detail.checklist];
                                                        cl[i] = { ...cl[i], done: !!v };
                                                        setDetail({ ...detail, checklist: cl });
                                                        await api.patch(`/tasks/${detail.id}`, { checklist: cl });
                                                    }}
                                                />
                                                <span className={c.done ? "line-through text-slate-400" : ""}>
                                                    {c.text}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="field-label mb-2 flex items-center gap-2">
                                    <MessageSquare size={12} /> Comments
                                </div>
                                <div className="space-y-2 max-h-52 overflow-y-auto">
                                    {(detail.comments || []).map((c) => (
                                        <div key={c.id} className="text-sm border rounded-md p-2 bg-white">
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium text-slate-700 text-xs">
                                                    {c.by_name}
                                                </span>
                                                <span className="text-xs text-slate-400">{fromNow(c.at)}</span>
                                            </div>
                                            <div className="text-slate-600 whitespace-pre-wrap mt-1">
                                                {c.text}
                                            </div>
                                        </div>
                                    ))}
                                    {(!detail.comments || detail.comments.length === 0) && (
                                        <div className="text-sm text-slate-400">No comments yet.</div>
                                    )}
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <Input
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="Add a comment…"
                                        data-testid="task-comment-input"
                                    />
                                    <Button onClick={addComment} className="bg-primary hover:bg-primary/90" data-testid="task-comment-send">
                                        Post
                                    </Button>
                                </div>
                            </div>

                            {detail.timeline?.length > 0 && (
                                <div>
                                    <div className="field-label mb-2">Timeline</div>
                                    <ol className="border-l border-slate-200 ml-1 space-y-2 text-xs">
                                        {detail.timeline.map((e, i) => (
                                            <li key={i} className="pl-4 relative">
                                                <span className="absolute -left-1 top-1 w-2 h-2 rounded-full bg-primary" />
                                                <span className="text-slate-700 font-medium capitalize">
                                                    {String(e.action).replace(/_/g, " ")}
                                                </span>
                                                <span className="text-slate-400 ml-2">{fromNow(e.at)}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}

                            {isAdmin && (
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setEditing(detail);
                                            setForm({ ...EMPTY, ...detail });
                                            setDetail(null);
                                            setOpen(true);
                                        }}
                                    >
                                        Edit task
                                    </Button>
                                </DialogFooter>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Rework dialog */}
            <Dialog open={reworkOpen} onOpenChange={setReworkOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Return for rework</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-500">
                        The employee will be notified with your reason.
                    </p>
                    <Textarea
                        rows={4}
                        placeholder="What needs to change?"
                        value={reworkReason}
                        onChange={(e) => setReworkReason(e.target.value)}
                        data-testid="rework-reason"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReworkOpen(false)}>Cancel</Button>
                        <Button
                            onClick={submitRework}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="rework-submit"
                        >
                            Send back
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
const Info = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
            {label}
        </div>
        <div className="text-slate-700">{value}</div>
    </div>
);
